import { useTranslation } from 'react-i18next';
import { formatYen } from '../../lib/revenue-aggregation.js';
import {
  NUM_CELL,
  OfferPeriodLabel,
  TH_BASE,
  useOfferBreakdownView,
} from './OfferBreakdownSection.js';

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 予実管理「広告・CPA」タブの「CPAに含めない割引」。オファーのうち合計CPA（オファーあり）に含めない割引（株主優待・福利厚生）を、クーポン名・理由・件数・金額（税抜）の表と合計行で出し、表の下に獲得数には含めオファーありの合計CPAには含めないことを注記する。一覧の全種類を、期間内に使われていなくても 0 件・0 円で並べる（並びは API が返す一覧の順）。期間・取得は「オファーの内訳」と同じ（同じ問い合わせ）で、見出しの横の期間も同じものを出す。オファーの内訳が取り込み済みで取得できたときだけ出し、計測前・期間の逆順・取り込み前・読み込み中・取得失敗は「オファーの内訳」の表示に任せて何も出さない。
 * @graph-connects useOfferBreakdownView [calls] 期間の埋め方・取得・見出し横の期間（オファーの内訳と共有）
 * @graph-connects OfferPeriodLabel [calls] 見出しの横の期間
 * @graph-connects formatYen [calls] 金額の書式（売上集計と共通）
 */
export function OfferExcludedSection({ from, to }: { from?: string; to?: string }) {
  const { t } = useTranslation();
  const { data, coveredThrough, shownPeriod, rangeLabel } = useOfferBreakdownView(from, to);
  if (data === undefined || coveredThrough === null) return null;
  const { excluded } = data;

  return (
    <section aria-labelledby="offer-excluded-title">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2
          id="offer-excluded-title"
          className="text-base font-semibold text-[var(--color-text-primary)]"
        >
          {t('budgetActual.offerBreakdown.excludedTitle')}
        </h2>
        <OfferPeriodLabel
          shownPeriod={shownPeriod}
          rangeLabel={rangeLabel}
          rangeTestId="offer-excluded-counted-range"
        />
      </div>
      <div className="overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
              <tr>
                <th className={`${TH_BASE} text-left`}>
                  {t('budgetActual.offerBreakdown.couponName')}
                </th>
                <th className={`${TH_BASE} text-left`}>
                  {t('budgetActual.offerBreakdown.excludedReason')}
                </th>
                <th className={`${TH_BASE} text-right`}>
                  {t('budgetActual.offerBreakdown.count')}
                </th>
                <th className={`${TH_BASE} text-right`}>
                  {t('budgetActual.offerBreakdown.excludedAmount')}
                </th>
              </tr>
            </thead>
            <tbody>
              {excluded.rows.map((row) => (
                <tr
                  key={row.couponName}
                  className="border-b border-[var(--color-border)] last:border-b-0"
                >
                  <td className="px-3.5 py-2.5 font-medium text-[var(--color-text-primary)]">
                    {row.couponName}
                  </td>
                  <td className="px-3.5 py-2.5 text-[12px] text-[var(--color-text-secondary)]">
                    {row.reason}
                  </td>
                  <td className={NUM_CELL}>{row.count.toLocaleString('ja-JP')}</td>
                  <td className={NUM_CELL}>{formatYen(row.offerAmount)}</td>
                </tr>
              ))}
              <tr className="border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] font-semibold">
                <td className="px-3.5 py-2.5 text-[var(--color-text-primary)]">
                  {t('budgetActual.offerBreakdown.total')}
                </td>
                <td className="px-3.5 py-2.5" />
                <td className={`${NUM_CELL} font-semibold`}>
                  {excluded.totalCount.toLocaleString('ja-JP')}
                </td>
                <td className={`${NUM_CELL} font-semibold`}>
                  {formatYen(excluded.totalOfferAmount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <p className="mt-2 text-xs text-[var(--color-text-muted)]">
        {t('budgetActual.offerBreakdown.excludedNote')}
      </p>
    </section>
  );
}
